import React, { useState, useEffect } from 'react';
import {
  Brain, MessageCircle, LogOut, TrendingUp,
  Zap, Sparkles, Crown, Phone, Shirt, Wand2,
  Image as ImageIcon, Clapperboard, Megaphone, Palette,
} from 'lucide-react';
import CommentMonitoring from './CommentMonitoring';
import LiveMonitoring from './LiveMonitoring';
import PostBoost from './PostBoost';
import SubscriptionPage from './SubscriptionPage';
import VideoAdCreator from './VideoAdCreator';
import MarketingAI from './MarketingAI';
import PageSelector from './PageSelector';
import MarketingStudio from './MarketingStudio';
import { API_URL } from '../../config';
import ProtectedMarketingStudio from './ProtectedMarketingStudio';
import IAStudioPanel from './IAStudioPanel';
import IAStudioVideoPanel from './IAStudioVideoPanel';
import CreditsPurchase from './CreditsPurchase';
import FacebookPageConnect from './FacebookPageConnect';
import WhatsAppConnect from './WhatsAppConnect';

// Vrais logos de marque (simple-icons, MIT) - lucide-react n'a pas d'icones Facebook/WhatsApp,
// on les incruste en SVG pour representer fidelement ces deux integrations plutot que des
// icones generiques (Share2/MessageCircle) qui ne les evoquent pas du tout.
const FacebookIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z" />
  </svg>
);
const WhatsappIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

// Robot Vidéo Animé en cercle
const RobotVideo = () => (
  <div className="relative w-40 h-40 mx-auto">
    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
    
    <div className="relative z-10 w-40 h-40 rounded-full overflow-hidden border-4 border-gradient-to-br from-blue-500 via-purple-500 to-cyan-500 shadow-2xl">
      <video
        autoPlay
        loop
        muted
        playsInline
        className="w-full h-full object-cover"
      >
        <source src="/Teckia-face.mp4" type="video/mp4" />
      </video>
    </div>
    
    <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full animate-ping" style={{animationDuration: '2s'}}></div>
    <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full animate-ping" style={{animationDuration: '3s', animationDelay: '1s'}}></div>
    
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="absolute w-2 h-2 bg-blue-400/50 rounded-full animate-float"
        style={{
          top: `${Math.random() * 150}%`,
          left: `${Math.random() * 150}%`,
          animationDelay: `${i * 0.3}s`,
          animationDuration: `${3 + Math.random() * 2}s`
        }}
      />
    ))}
  </div>
);

// Background animé avec particles
const AnimatedBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-float"></div>
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-float-delayed"></div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
    <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
    {[...Array(20)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-blue-400/30 rounded-full animate-float"
        style={{
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          animationDelay: `${i * 0.5}s`,
          animationDuration: `${5 + Math.random() * 5}s`
        }}
      />
    ))}
  </div>
);

// Sélectionne un motif de fond différent par module (déterministe via le titre), pour que
// chaque boîte soit visuellement distincte structurellement, pas juste par sa couleur.
const CARD_PATTERNS = ['dots', 'diag', 'hex', 'wave'];
const patternFor = (title) => {
  let h = 0;
  for (let i = 0; i < title.length; i++) h = (h * 31 + title.charCodeAt(i)) >>> 0;
  return CARD_PATTERNS[h % CARD_PATTERNS.length];
};

const CardPatternOverlay = ({ pattern, uid }) => {
  if (pattern === 'dots') {
    return <div className="absolute inset-0 opacity-[0.14] [background-image:radial-gradient(circle,rgba(255,255,255,0.7)_1px,transparent_1px)] [background-size:16px_16px]" />;
  }
  if (pattern === 'diag') {
    return <div className="absolute inset-0 opacity-[0.07] [background-image:repeating-linear-gradient(45deg,rgba(255,255,255,0.8)_0px,rgba(255,255,255,0.8)_1px,transparent_1px,transparent_13px)]" />;
  }
  if (pattern === 'hex') {
    const id = `hexpat-${uid}`;
    return (
      <svg className="absolute inset-0 w-full h-full opacity-[0.10]" preserveAspectRatio="none">
        <defs>
          <pattern id={id} width="26" height="22" patternUnits="userSpaceOnUse">
            <path d="M13 0 L26 7 L26 18 L13 25 L0 18 L0 7 Z" fill="none" stroke="white" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${id})`} />
      </svg>
    );
  }
  return (
    <svg className="absolute bottom-0 left-0 w-full h-28 opacity-[0.10]" viewBox="0 0 400 100" preserveAspectRatio="none">
      <path d="M0,55 C100,95 300,15 400,55 L400,100 L0,100 Z" fill="white" />
    </svg>
  );
};

// Service Card — style "boîte 3D" avec fond dégradé unique par module
const ServiceCard = ({ icon: Icon, iconImage, title, description, isActive, onClick, color, badge }) => {
  const pattern = patternFor(title);
  const uid = title.replace(/[^a-zA-Z0-9]/g, '');
  return (
  <div onClick={onClick} className="group relative cursor-pointer" style={{ perspective: '1200px' }}>
    <div
      className={`relative overflow-hidden rounded-3xl p-6 md:p-8 transition-all duration-500 ease-out group-hover:-translate-y-3 group-hover:scale-[1.025] ${
        isActive
          ? 'bg-[#0c0a18] border border-white/10'
          : 'bg-white/5 border border-white/10 group-hover:bg-white/[0.07]'
      }`}
      style={{
        boxShadow: isActive
          ? '0 2px 6px rgba(0,0,0,0.5), 0 20px 45px -18px rgba(0,0,0,0.75)'
          : '0 2px 6px rgba(0,0,0,0.3)',
      }}
    >
      {/* Mesh de fond — 3 halos superposés à des positions différentes pour un effet "pro" */}
      <div className={`absolute -top-20 -right-16 w-64 h-64 rounded-full bg-gradient-to-br ${color} opacity-25 blur-3xl group-hover:opacity-45 group-hover:scale-110 transition-all duration-700`} />
      <div className={`absolute -bottom-24 -left-14 w-48 h-48 rounded-full bg-gradient-to-tr ${color} opacity-10 blur-3xl group-hover:opacity-20 transition-opacity duration-700`} />
      <div className={`absolute top-1/3 left-1/2 w-32 h-32 rounded-full bg-gradient-to-r ${color} opacity-[0.08] blur-2xl`} />
      {/* Motif géométrique propre à ce module (dots/diagonales/hexagones/vague) */}
      <CardPatternOverlay pattern={pattern} uid={uid} />
      {/* Icône géante en filigrane, signature visuelle forte par module */}
      {iconImage ? (
        <img src={iconImage} alt="" className="absolute -bottom-8 -right-8 w-44 h-44 rounded-full object-cover opacity-[0.07] rotate-[8deg] pointer-events-none" />
      ) : (
        <Icon className="absolute -bottom-8 -right-8 w-44 h-44 text-white/[0.04] rotate-[8deg] pointer-events-none" strokeWidth={1} />
      )}
      {/* Grille fine en surimpression */}
      <div className="absolute inset-0 opacity-40 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:22px_22px]" />
      {/* Liseré lumineux au survol */}
      <div className={`absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ring-1 ring-inset ring-white/20`} />
      {/* Reflet balayant */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

      <div className="relative z-10">
        {/* Icône en orbe 3D avec reflet glossy */}
        <div
          className={`relative w-16 h-16 md:w-20 md:h-20 rounded-2xl overflow-hidden bg-gradient-to-br ${color} flex items-center justify-center mb-5 md:mb-7 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500`}
          style={{ boxShadow: '0 10px 25px -8px rgba(0,0,0,0.7), inset 0 1px 1px rgba(255,255,255,0.25)' }}
        >
          {iconImage ? (
            <img src={iconImage} alt={title} className="w-full h-full object-cover" />
          ) : (
            <Icon className="w-8 h-8 md:w-10 md:h-10 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]" />
          )}
          <div className="absolute top-2 left-2.5 w-6 h-3.5 rounded-full bg-white/40 blur-[5px]" />
        </div>

        <h3 className="text-lg md:text-2xl font-bold text-white mb-2">{title}</h3>
        <p className="text-gray-400 mb-4 md:mb-6 text-xs md:text-sm">{description}</p>

        {badge && (
          <div className="mb-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-500/15 text-yellow-300 border border-yellow-500/25">
              {badge}
            </span>
          </div>
        )}

        <div className={`inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-medium ${
          isActive
            ? 'bg-green-500/15 text-green-300 border border-green-500/25'
            : 'bg-white/10 text-white/60'
        }`}>
          <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-400 animate-pulse' : 'bg-white/40'}`}></div>
          {isActive ? 'Actif' : 'Non abonné'}
        </div>
      </div>
    </div>
  </div>
  );
};

// Message "Pas d'abonnement"
const NoSubscriptionMessage = ({ onSubscribe, onLogout }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 relative overflow-hidden">
    <AnimatedBackground />
    
    <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-40">
      <div className="container mx-auto px-4 py-4 md:py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/TeckiA-logo.gif" 
              alt="TeckiA IA" 
              className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
            />
            <div>
              <h1 className="text-base md:text-xl font-bold text-white flex items-center gap-2">
                TeckiA IA
                <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-yellow-400 animate-pulse" />
              </h1>
              <p className="text-xs md:text-sm text-blue-200">
                Bonjour, {localStorage.getItem('user_name')}
              </p>
            </div>
          </div>
          
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all hover:scale-105 text-sm md:text-base"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>
      </div>
    </header>
    
    <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)] p-4 md:p-8">
      <div className="max-w-4xl w-full text-center">
        <RobotVideo />
        
        <h2 className="text-4xl md:text-6xl font-bold mt-8 mb-4">
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
            Salut, TeckiA est à ton service  
          </span>
        </h2>
        
        <p className="text-lg md:text-2xl text-purple-200 mb-8">
          Aucun service actif pour le moment
        </p>
        
        <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 md:p-12 border border-white/10 mb-8">
          <Crown className="w-16 h-16 md:w-20 md:h-20 text-yellow-400 mx-auto mb-6 animate-bounce" />
          
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Découvrez nos services intelligents
          </h3>
          
          <p className="text-base md:text-lg text-white/70 mb-8 max-w-2xl mx-auto">
            Automatisez vos réponses, gérez vos conversations, boostez vos ventes avec l'intelligence artificielle
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="text-3xl mb-2">🤖</div>
              <div className="text-white font-medium text-sm md:text-base">Bot IA Intelligent</div>
            </div>
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="text-3xl mb-2">💬</div>
              <div className="text-white font-medium text-sm md:text-base">Surveillance Auto</div>
            </div>
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="text-3xl mb-2">🎨</div>
              <div className="text-white font-medium text-sm md:text-base">Marketing IA</div>
            </div>
          </div>
          
          <button
            onClick={onSubscribe}
            className="px-8 md:px-12 py-4 md:py-6 rounded-2xl bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 hover:from-purple-700 hover:via-pink-700 hover:to-cyan-700 text-white font-bold text-base md:text-xl transition-all hover:scale-110 shadow-2xl animate-pulse"
          >
            <span className="flex items-center gap-3 justify-center">
              <Sparkles className="w-5 h-5 md:w-6 md:h-6" />
              Voir les abonnements
              <Sparkles className="w-5 h-5 md:w-6 md:h-6" />
            </span>
          </button>
        </div>
        
        <p className="text-sm md:text-base text-white/40">
          Essai gratuit disponible • Sans engagement • Support 24/7
        </p>
      </div>
    </div>
  </div>
);

export default function ClientDashboard({ onLogout }) {
  const [view, setView] = useState('dashboard');
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [activePagesCount, setActivePagesCount] = useState({
    teckia_vtryon: 0,
    teckia_universal: 0,
    messenger_bot: 0,
    comment_monitoring: 0
  });
  const [creditsSummary, setCreditsSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const ALL_SERVICES = [
    // 'Messenger Bot IA' supprimé : remplacé par « Page Facebook »
    // (connexion officielle + agent IA + ventes + conversations + prise de main)
    {
      id: 'ia_studio',
      icon: ImageIcon,
      iconImage: '/icons/image-gen.png',
      title: 'TeckiA Image Studio',
      description: 'Génération image IA — 600 Ar/image',
      color: 'from-fuchsia-600 to-purple-600',
      component: 'ia_studio',
      badge: '💰 Crédits'
    },
    {
      id: 'ia_studio_video',
      icon: Clapperboard,
      iconImage: '/icons/video-gen.png',
      title: 'TeckiA IA Studio Vidéo',
      description: 'Génération vidéo + storyboard scène par scène',
      color: 'from-indigo-600 to-fuchsia-600',
      component: 'ia_studio_video',
      badge: '🎬 Crédits'
    },
    {
      id: 'video_ads',
      icon: Megaphone,
      iconImage: '/icons/video-ads.png',
      title: 'TeckiA Pub Vidéos',
      description: 'Vidéos publicitaires IA 30s',
      color: 'from-red-600 to-orange-600',
      component: 'video_ads',
      badge: '🎬 Nouveau'
    },
    {
      id: 'marketing_studio',
      icon: Palette,
      iconImage: '/icons/marketing-studio.png',
      title: 'Marketing-Studio',
      description: 'Images Pub & Vidéos',
      color: 'from-purple-600 to-pink-600',
      component: 'ambondrombe'
    },
    // 'TeckiA VTry-on' retiré temporairement : nécessite encore une page
    // active alors que le module a été migré en pool de crédits utilisateur.
    {
      id: 'teckia_universal',
      icon: Wand2,
      title: 'Universal Marketing',
      description: 'Marketing produits universel',
      color: 'from-cyan-600 to-blue-600',
      component: 'universal',
      badge: 'All Products'
    },
    {
      id: 'comment_monitoring',
      icon: MessageCircle,
      title: 'Surveillance Commentaires',
      description: 'Détection et réponse auto',
      color: 'from-orange-600 to-red-600',
      component: 'comment_monitoring'
    },
    {
      id: 'whatsapp_bot',
      icon: MessageCircle,
      title: 'WhatsApp Bot IA',
      description: 'Conversations intelligentes WhatsApp',
      color: 'from-green-600 to-emerald-600',
      component: 'whatsapp'
    },
    {
      id: 'ai_agent_alone',
      icon: Phone,
      title: 'Agent IA Réceptionniste',
      description: 'Répondeur vocal intelligent',
      color: 'from-purple-600 to-violet-600',
      component: 'ai_agent'
    },
    {
      id: 'ai_agent_website',
      icon: Brain,
      title: 'Agent IA + Site Web',
      description: 'Réceptionniste + Site vitrine',
      color: 'from-indigo-600 to-purple-600',
      component: 'ai_agent_web'
    }
  ];

  useEffect(() => {
    loadSubscriptions();
    loadActivePagesCount();
    loadCreditsSummary();
  }, []);

  const loadCreditsSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/credits-summary`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setCreditsSummary(data.credits);
      }
    } catch (e) {
      console.error('Erreur credits summary:', e);
    }
  };

  // Badge crédits affiché sur les cartes des modules à crédits
  const creditsBadge = (serviceId) => {
    if (!creditsSummary || !creditsSummary[serviceId]) return '💰 0 crédit';
    const c = creditsSummary[serviceId];
    if (c.ariary !== undefined) {
      return c.ariary > 0 ? `💰 ${c.ariary.toLocaleString('fr-FR')} Ar` : '💰 0 crédit — Achetez';
    }
    const parts = [];
    if (c.images !== undefined) parts.push(`📸 ${c.images} img`);
    if (c.videos !== undefined) parts.push(`🎬 ${c.videos} vid`);
    const total = (c.images || 0) + (c.videos || 0);
    return total > 0 ? parts.join(' · ') : '💰 0 crédit — Achetez';
  };

  const loadSubscriptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/subscriptions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📦 Subscriptions received:', data);
        
        const subs = data.subscriptions || [];
        
        const activeSubs = subs.filter(sub => {
          if (sub.status === 'active' && sub.setup_status === 'completed') {
            return true;
          }
          if (
            sub.service_id === 'teckia_vtryon' || 
            sub.service_id === 'teckia_universal' ||
            sub.service_id === 'video_ads' ||        // ✅ AJOUTÉ
            sub.service_id === 'marketing_studio'    // ✅ AJOUTÉ
          ) {
            return sub.status === 'active';
          }
          return false;
        });
        
        console.log('✅ Active subscriptions:', activeSubs);
        setActiveSubscriptions(activeSubs);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivePagesCount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const pages = data.clients || [];
        
        const counts = {
          teckia_vtryon: 0,
          teckia_universal: 0,
          messenger_bot: 0,
          comment_monitoring: 0
        };
        
        pages.forEach(page => {
          if (
            (page.max_images_studiopro > 0) ||
            (page.max_videos_wan > 0) ||
            (page.max_videos_hailua > 0)
          ) {
            counts.teckia_vtryon++;
          }
          
          if (
            (page.max_images_universal > 0) ||
            (page.max_videos_universal > 0)
          ) {
            counts.teckia_universal++;
          }
          
          counts.messenger_bot++;
        });
        
        try {
          const monitoringRes = await fetch(`${API_URL}/api/comment-monitoring/active-pages`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
          });
          if (monitoringRes.ok) {
            const monitoringData = await monitoringRes.json();
            counts.comment_monitoring = monitoringData.count || 0;
          }
        } catch (err) {
          console.log('⚠️ Comment monitoring count not available:', err);
        }
        
        console.log('📊 Active pages count:', counts);
        setActivePagesCount(counts);
      }
    } catch (error) {
      console.error('Error loading pages:', error);
    }
  };

  // Modules 100% CRÉDITS : toujours visibles, utilisables selon les crédits achetés
  const CREDIT_SERVICES = ['video_ads', 'marketing_studio', 'teckia_vtryon', 'ia_studio', 'ia_studio_video'];

  const isServiceActive = (serviceId) => {
    // Modèle crédits : ces modules sont toujours affichés (0 crédit au départ)
    if (CREDIT_SERVICES.includes(serviceId)) return true;
    
    const hasSub = activeSubscriptions.some(sub => sub.service_id === serviceId);
    
    console.log(`🔍 isServiceActive(${serviceId}):`, {
      hasSub,
      activePagesCount: activePagesCount[serviceId],
      subscriptions: activeSubscriptions.filter(s => s.service_id === serviceId)
    });
    
    if (serviceId === 'teckia_vtryon' || serviceId === 'teckia_universal') {
      const isActive = hasSub && (activePagesCount[serviceId] > 0);
      console.log(`✅ ${serviceId} active:`, isActive);
      return isActive;
    }
    
    if (serviceId === 'comment_monitoring') {
      const isActive = hasSub && (activePagesCount.comment_monitoring > 0);
      console.log(`✅ ${serviceId} active:`, isActive);
      return isActive;
    }
    
    console.log(`✅ ${serviceId} active:`, hasSub);
    return hasSub;
  };

  const activeServices = ALL_SERVICES.filter(s => isServiceActive(s.id));


  if (view === 'ambondrombe') {
    return <ProtectedMarketingStudio onBack={() => setView('dashboard')} />;
  }

  if (view === 'ia_studio') {
    return <IAStudioPanel onBack={() => setView('dashboard')} />;
  }

  if (view === 'ia_studio_video') {
    return <IAStudioVideoPanel onBack={() => setView('dashboard')} />;
  }

  if (view === 'subscription') {
    return <SubscriptionPage onBack={() => { setView('dashboard'); loadSubscriptions(); }} />;
  }

  if (view === 'credits') {
    return <CreditsPurchase onBack={() => { setView('dashboard'); loadSubscriptions(); }} />;
  }

  if (view === 'video_ads') {
    return <VideoAdCreator onBack={() => setView('dashboard')} />;
  }

  if (view === 'vtryon' || view === 'universal') {
    return <PageSelector serviceType={view} onBack={() => setView('dashboard')} />;
  }

  if (view === 'comment_monitoring') {
    return <PageSelector serviceType="comment_monitoring" onBack={() => setView('dashboard')} />;
  }

  if (view === 'fb_connect') return <FacebookPageConnect onBack={() => setView('dashboard')} />;
  if (view === 'whatsapp_connect') return <WhatsAppConnect onBack={() => setView('dashboard')} />;
  if (view === 'live') return <LiveMonitoring onBack={() => setView('dashboard')} />;
  if (view === 'boost') return <PostBoost onBack={() => setView('dashboard')} />;
  if (view === 'marketing') return <MarketingAI onBack={() => setView('dashboard')} />;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-white text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  if (activeServices.length === 0) {
    return <NoSubscriptionMessage onSubscribe={() => setView('subscription')} onLogout={onLogout} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 relative overflow-hidden">
      <AnimatedBackground />
      
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="/TeckiA-logo.gif" 
                alt="TeckiA IA" 
                className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
              />
              <div>
                <img 
                  src="/name_teckia.png" 
                  alt="TeckiA" 
                  className="w-24 h-10 md:w-32 md:h-12 object-contain"
                />
                <p className="text-xs md:text-sm text-blue-200">
                  Bonjour, {localStorage.getItem('user_name')}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">

                    <button
          onClick={() => setView('credits')}
          className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 text-yellow-300 border border-yellow-500/30 transition-all hover:scale-105 text-sm md:text-base group"
        >
          <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
          <span className="hidden sm:inline">Acheter Crédits</span>
          <span className="sm:hidden">Crédits</span>
        </button>

              <button
                onClick={onLogout}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all hover:scale-105 text-sm md:text-base"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 md:py-12 relative z-10">
        <div className="text-center mb-8 md:mb-12 relative">
          <RobotVideo />
          
          <h2 className="text-3xl md:text-6xl font-bold mt-6 md:mt-8 mb-3 md:mb-4">
            😎 <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Salut, c'est TeckiA  
            </span> 🤖
          </h2>
          <p className="text-base md:text-2xl text-blue-200 mb-2">
            {activeServices.length} service{activeServices.length > 1 ? 's' : ''} actif{activeServices.length > 1 ? 's' : ''}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-12">
          {/* Connexion Page Facebook — connexion, agent IA, ventes, conversations, alertes */}
          <ServiceCard
            id="fb_connect"
            icon={FacebookIcon}
            title="Page Facebook"
            description="Agent IA, ventes, conversations et alertes de votre page"
            color="from-blue-600 to-cyan-600"
            badge="🔗 Connexion officielle"
            isActive={true}
            onClick={() => setView('fb_connect')}
          />
          {/* Connexion WhatsApp — même agent IA, service géré par un humain */}
          <ServiceCard
            id="whatsapp_connect"
            icon={WhatsappIcon}
            title="WhatsApp"
            description="Agent IA, ventes, conversations et alertes sur WhatsApp"
            color="from-green-600 to-emerald-600"
            badge="🟢 Nouveau"
            isActive={true}
            onClick={() => setView('whatsapp_connect')}
          />
          {activeServices.map((service) => (
            <ServiceCard
              key={service.id}
              {...service}
              badge={CREDIT_SERVICES.includes(service.id) ? creditsBadge(service.id) : service.badge}
              isActive={true}
              onClick={() => setView(service.component)}
            />
          ))}
        </div>

      </main>
    </div>
  );
}